import { createContext, useContext } from "react";
import type { MessageFlagKind } from "../../lib/api.js";
import type { PendingMessage } from "./types.js";

/**
 * What a message row can do, and what it needs to know about the viewer.
 *
 * Provided once by the chat view and read by each MessageRun, rather than
 * passed down through ThreadPanel and MessageList, neither of which uses any
 * of it. A new per-message action (pin, forward text, open thread) is one
 * field here and one button in MessageRun.
 */
export interface MessageActions {
  toggleReaction: (messageId: string, emoji: string) => void;
  raiseTask: (message: PendingMessage) => void;
  reply: (message: PendingMessage) => void;
  edit: (message: PendingMessage) => void;
  delete: (message: PendingMessage) => void;
  setFlag: (messageId: string, kind: MessageFlagKind | null) => void;
  remind: (messageId: string, at: Date) => void;
  forward: (message: PendingMessage) => void;
  retry: (message: PendingMessage) => void;
  /** Opens the reply-thread panel for a message (its root, if it is itself a reply). */
  openThread: (message: PendingMessage) => void;
  togglePin: (messageId: string, pinned: boolean) => void;
  toggleSaved: (messageId: string, saved: boolean) => void;
  /** Author only: hides the link preview from everyone. */
  removePreview: (messageId: string) => void;
  /** Whose view this is, so their own runs and mentions of them can be marked. */
  viewerId: string | undefined;
  canRaiseTask: boolean;
  /** Pinning is for members; someone only reading an open channel cannot. */
  canPin: boolean;
  /** Newest message id everyone else has read past; null if anyone is behind. */
  readWatermark: string | null;
  /** The message a search result just jumped to, flashed once. */
  jumpedId: string | null;
}

export const MessageActionsContext = createContext<MessageActions | null>(null);

export function useMessageActions(): MessageActions {
  const actions = useContext(MessageActionsContext);
  if (!actions) throw new Error("useMessageActions must be used inside MessageActionsContext");
  return actions;
}
